import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IntakeFormTemplate, canManageForms } from '../../../types/formBuilder';
import { getAllForms, deleteForm } from '../../../firebase/formManagement';
import { User } from '../../../types';
import { PatientSearchModal } from '../../dashboard/PatientSearchModal';
import { DynamicIntakeFormModal } from './DynamicIntakeFormModal';

interface FormManagerProps {
  user: User;
  searchTerm?: string;
}

export function FormManager({ user, searchTerm: externalSearchTerm = '' }: FormManagerProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [forms, setForms] = useState<IntakeFormTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>('all');
  const searchTerm = externalSearchTerm;
  const [showPatientSearch, setShowPatientSearch] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [showFormModal, setShowFormModal] = useState(false);

  const loadForms = async () => {
    setIsLoading(true);
    try {
      const allForms = await getAllForms();
      
      // Filter forms based on user permissions
      let filteredForms = allForms;
      
      if (user) {
        // Get user's organization role
        const userOrgRole = user.organizationRole;
        const isSuperAdmin = user.role === 'super_admin';
        
        // Super admins can see all forms
        if (!isSuperAdmin && userOrgRole) {
          filteredForms = allForms.filter(form => {
            // If form is shared with 'all', everyone can see it
            if (form.sharedWith === 'all') {
              return true;
            }
            
            // If form is shared with 'specific', check allowedRoles
            if (form.sharedWith === 'specific') {
              // If no allowedRoles specified, allow all (backward compatibility)
              if (!form.allowedRoles || form.allowedRoles.length === 0) {
                return true;
              }
              
              // Check if user's role is in allowedRoles
              return form.allowedRoles.includes(userOrgRole);
            }
            
            // Default: allow if no sharing setting (backward compatibility)
            return true;
          });
        }
      }
      
      setForms(filteredForms);
    } catch (error) {
      alert('Failed to load forms');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateForm = () => {
    navigate('/form-builder');
  };

  const handleEditForm = (formId: string) => {
    if (canManageForms(user.role)) {
      navigate(`/form-builder/${formId}`);
    }
  };

  const handleViewForm = (formId: string) => {
    // Show patient search modal first
    setSelectedFormId(formId);
    setShowPatientSearch(true);
  };

  const handlePatientSelect = (patient: any) => {
    const caseId = patient.caseId || patient.id;
    setSelectedCaseId(caseId);
    setShowPatientSearch(false);
    setShowFormModal(true);
  };

  const handleDeleteForm = async (formId: string, formName: string) => {
    try {
      const result = await deleteForm(formId, user.role);
      if (result.success) {
        alert(t('formManager.deleteSuccess'));
        loadForms(); // Reload forms
      } else {
        alert(result.error || t('formManager.deleteFailed'));
      }
    } catch (error) {
      alert(t('formManager.deleteFailed'));
    }
  };

  // Filter forms by type and search term
  const filteredForms = forms.filter(form => {
    // Filter by type
    const matchesType = filterType === 'all' || form.type === filterType;
    
    // Filter by search term
    const matchesSearch = !searchTerm || 
      (i18n.language === 'he' ? (form.nameHe || form.name) : (form.nameEn || form.name))
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (form.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesType && matchesSearch;
  });

  const hasManagePermission = canManageForms(user.role);

  if (isLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#666' }}>
          {t('common.loading')}...
        </div>
      </div>
    );
  }

  return (
    <div className="form-manager-container" style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          marginBottom: '24px'
        }}>
          <h1 style={{ fontSize: '24px', marginBottom: '8px', color: '#000' }}>
            {t('formManager.title')}
          </h1>
          <p style={{ color: '#666', margin: 0 }}>
            {t('formManager.subtitle')}
          </p>
        </div>

      {/* Filter */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
          {t('formManager.filterByType')}:
        </label>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          style={{
            padding: '10px 12px',
            border: '1px solid #ced4da',
            borderRadius: '6px',
            fontSize: '14px',
            minWidth: '200px'
          }}
        >
          <option value="all">{t('formManager.allForms')}</option>
          <option value="rights">{t('formBuilder.rightsForm')}</option>
          <option value="emotional">{t('formBuilder.emotionalForm')}</option>
          <option value="professional">{t('formBuilder.professionalForm')}</option>
          <option value="custom">{t('formBuilder.customForm')}</option>
        </select>
      </div>

      {/* Forms List */}
      {filteredForms.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          background: 'white',
          borderRadius: '8px',
          border: '1px solid #e9ecef'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>📋</div>
          <h3 style={{ color: '#666', marginBottom: '8px' }}>
            {t('formManager.noForms')}
          </h3>
          <p style={{ color: '#999', marginBottom: '20px' }}>
            {t('formManager.createFirstForm')}
          </p>
          {hasManagePermission && (
            <button
              onClick={handleCreateForm}
              style={{
                background: '#007acc',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              + {t('formManager.createForm')}
            </button>
          )}
        </div>
      ) : (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '20px'
        }}>
          {filteredForms.map(form => (
            <div
              key={form.id}
              style={{
                background: 'white',
                border: '1px solid #e9ecef',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
              }}
            >
              {/* Form Name */}
              <h3 style={{ 
                fontSize: '18px', 
                marginBottom: '8px',
                marginTop: '0',
                color: '#000'
              }}>
                {i18n.language === 'he' ? (form.nameHe || form.name) : (form.nameEn || form.name)}
              </h3>

              {/* Description */}
              {form.description && (
                <p style={{ 
                  fontSize: '13px', 
                  color: '#666', 
                  marginBottom: '12px',
                  lineHeight: '1.4',
                  flex: 1
                }}>
                  {form.description}
                </p>
              )}

              {/* Stats */}
              <div style={{ 
                display: 'flex', 
                gap: '16px', 
                marginBottom: '16px',
                paddingTop: '12px',
                borderTop: '1px solid #e9ecef',
                fontSize: '13px',
                color: '#666'
              }}>
                <div>
                  📝 {form.fields.length} {t('formManager.fields')}
                </div>
                <div>
                  v{form.version}
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ 
                display: 'flex',
                gap: '8px',
                marginTop: 'auto'
              }}>
                {hasManagePermission ? (
                  <>
                    <button
                      onClick={() => handleEditForm(form.id)}
                      style={{
                        background: '#007acc',
                        color: 'white',
                        border: 'none',
                        padding: '10px 16px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500',
                        flex: 1
                      }}
                    >
                      {t('common.edit')}
                    </button>
                    <button
                      onClick={() => handleDeleteForm(form.id, form.name)}
                      style={{
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        padding: '10px 16px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500',
                        flex: 1
                      }}
                    >
                      {t('common.delete')}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleViewForm(form.id)}
                    style={{
                      background: '#007acc',
                      color: 'white',
                      border: 'none',
                      padding: '10px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      width: '100%'
                    }}
                  >
                    {t('formManager.viewForm') || (i18n.language === 'he' ? 'השתמש בטופס' : 'Use Form')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Patient Search Modal */}
      {showPatientSearch && (
        <PatientSearchModal
          title={t('formManager.selectPatientForForm') || 'Select Patient for Form'}
          placeholder={t('formManager.searchPatientForForm') || 'Search for patient by name, email, or case ID...'}
          onClose={() => {
            setShowPatientSearch(false);
            setSelectedFormId(null);
          }}
          onSelect={handlePatientSelect}
        />
      )}

      {/* Form Modal */}
      {showFormModal && selectedFormId && selectedCaseId && (
        <DynamicIntakeFormModal
          formId={selectedFormId}
          caseId={selectedCaseId}
          isOpen={showFormModal}
          readOnly={false}
          onClose={() => {
            setShowFormModal(false);
            setSelectedFormId(null);
            setSelectedCaseId(null);
          }}
          onSuccess={() => {
            setShowFormModal(false);
            setSelectedFormId(null);
            setSelectedCaseId(null);
          }}
        />
      )}
    </div>
  );
}



export default FormManager;

